/* eslint-disable node/prefer-global/process */
import type { DetailsSuccess } from '@autobid/strapi-integration/typescript/auction/detailsCar'
import type { QueryKey, UseQueryOptions } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useDetailsSocket } from '@autobid/ui/composables/car/sockets/useDetailsSocket'
import { useMyPreviewMode } from '@autobid/ui/composables/useMyPreviewMode'
import { CAR_PREVIEW_PDF_DATA } from '@autobid/ui/constants/CAR_PREVIEW_PDF_DATA'
import { generateImageSizesFromObject } from '@autobid/ui/utils/car/generateImageSizes'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, onMounted, unref } from 'vue'

// #region constants
const CACHE_TIME = 1000 * 60 * 60
// #endregion

// #region internal functions
function prepareCarData(data: DetailsSuccess['data']['singleItem']) {
  return {
    ...data,
    item: {
      ...data.item,
      itemData: {
        ...data.item.itemData,
        imageGroups: generateImageSizesFromObject(
          'hd',
          data.item.itemData.imageGroups,
        ),
      },
    },
  }
}

function useFetchDetailsCar() {
  const { $trpc } = useNuxtApp()
  const { locale } = useI18n()
  const { enabled: isPreview } = useMyPreviewMode()

  const fetchDetailsCar = async (
    carId: number,
  ): Promise<DetailsSuccess['data']['singleItem']> => {
    if (isPreview.value) {
      return prepareCarData(CAR_PREVIEW_PDF_DATA)
    }

    const result = await $trpc.auctions.getDetailsCar.query({ carId, lang: locale.value })

    if (!result) {
      throw createError({ statusCode: 404, statusMessage: 'No response' })
    }

    if ('errors' in result) {
      const { message: statusMessage, status_code: statusCode }
        = result.errors[0]

      throw createError({ statusCode, statusMessage })
    }

    if ('error_code' in result.data.singleItem.metadata) {
      const { message: statusMessage, error_code: statusCode }
        = result.data.singleItem.metadata

      throw createError({ statusCode, statusMessage })
    }

    if (!result.data.singleItem.item) {
      throw createError({ statusCode: 404, statusMessage: 'No data' })
    }

    return prepareCarData(
      result.data.singleItem as DetailsSuccess['data']['singleItem'],
    )
  }

  return { fetchDetailsCar }
}
// #endregion

export const carDetailsQueryKey = {
  all: () => ['car-details'] as const,
  byCarId: (carId: number) => ['car-details', carId] as const,
}

export function usePrefetchDetailsCar() {
  const { fetchDetailsCar } = useFetchDetailsCar()
  const queryClient = useQueryClient()
  const { init: initSockets } = useDetailsSocket()
  onMounted(initSockets)

  const prefetchDetailsCar = async (carId: number) => {
    await queryClient.prefetchQuery({
      queryKey: carDetailsQueryKey.byCarId(carId) as QueryKey,
      queryFn: async () => {
        try {
          return await fetchDetailsCar(carId)
        }
        catch {
          return null
        }
      },
      staleTime: CACHE_TIME,
      ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    })

    return queryClient.getQueryData<ReturnType<typeof fetchDetailsCar>>(carDetailsQueryKey.byCarId(carId))
  }

  return { prefetchDetailsCar }
}

export function useDetailsCar(carId: Ref<number>, options?: UseQueryOptions<DetailsSuccess['data']['singleItem'] | null, Error>) {
  const { fetchDetailsCar } = useFetchDetailsCar()
  const { init: initSockets } = useDetailsSocket()
  onMounted(initSockets)

  return useQuery({
    queryKey: computed(() => carDetailsQueryKey.byCarId(unref(carId))),
    queryFn: async () => {
      try {
        return await fetchDetailsCar(unref(carId))
      }
      catch (error) {
        const { message, code, handled } = getError(error)

        if (!handled) {
          throw createError({ statusCode: code, statusMessage: message })
        }

        return null
      }
    },
    staleTime: CACHE_TIME,
    ...(process.client ? { cacheTime: CACHE_TIME } : {}),
    refetchOnWindowFocus: false,
    ...(options || {}),
  })
}
